import type { Configuration } from '@azure/msal-browser';
import { getEnvFromUrl } from '@newfront-insurance/shared-public-config';

import { getConfig } from './index';

/**
 * Centralized MS Graph API configuration.
 * All hardcoded values and detection logic live here - components should never pass config.
 */

// MS Graph API Constants
const TENANT_ID = 'c42d9be5-6265-4aef-baea-ea96fb661589';
export const MSAL_SCOPES = ['user.read', 'mail.readwrite', 'mail.send'];

/**
 * Gets the client ID from shared config.
 */
function getClientId(): string {
  const config = getConfig();
  const novaAddinsConfig = config.NOVA_ADDINS as { CLIENT_ID: string } | undefined;

  if (!novaAddinsConfig?.CLIENT_ID) {
    throw new Error('[MS Graph Auth] CLIENT_ID not found in config.NOVA_ADDINS');
  }

  return novaAddinsConfig.CLIENT_ID;
}

/**
 * Dynamically determines the MSAL redirect URI based on the current environment
 * and application context (Outlook vs Navigator).
 *
 * This function automatically detects:
 * - Environment (localhost, dev, stage, production)
 * - Application context (Nova Outlook vs Navigator) based on URL path
 *
 * @returns The appropriate redirect URI for MSAL authentication
 */
function getRedirectUri(): string {
  const newfrontEnv: string | undefined = getEnvFromUrl() || 'localhost';

  const config = getConfig();
  const baseUrl = newfrontEnv === 'localhost' ? 'https://dash.local.internal' : config.LINKS.DASH_APP;

  // If running in nova-addins/outlook, use that path as redirect URI
  const redirectPath = typeof window !== 'undefined' ? window.location.pathname : '';
  if (redirectPath.includes('/nova-addins/outlook')) {
    return `${baseUrl}/nova-addins/outlook`;
  }

  // Otherwise use base URL (for dashboard apps including Navigator)
  return baseUrl;
}

/**
 * Creates MSAL configuration with all settings auto-detected.
 * No parameters needed - everything is determined from environment and context.
 */
export function createMsalConfig(): Configuration {
  return {
    auth: {
      clientId: getClientId(),
      authority: `https://login.microsoftonline.com/${TENANT_ID}`,
      redirectUri: getRedirectUri(),
    },
  };
}
